// packages/web/src/components/teach/ExerciseCardsSection.tsx
import React, { useState } from 'react';
import { useTeacherLearningStore } from '../../stores/teacherLearningStore';
import { teacherLearningApi } from '../../services/teacherLearningApi';
import { ExerciseCard } from './ExerciseCard';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

export const ExerciseCardsSection: React.FC = () => {
  const {
    exercises,
    exerciseImagePath,
    currentStep,
    step3Status,
    updateExercise,
    toggleExerciseSelection,
    selectAllExercises,
    deselectAllExercises,
    deleteExercise,
    setExercises,
    setStepStatus,
    setErrorMessage,
    setSelectedExerciseForDetail,
    setShowKnowledgePointsModal,
    setShowAssignModal,
  } = useTeacherLearningStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const { toast, success, error, hideToast } = useToast();

  // 处理全选/取消全选
  const handleToggleSelectAll = () => {
    const allSelected = exercises.every((ex) => ex.selected);
    if (allSelected) {
      deselectAllExercises();
    } else {
      selectAllExercises();
    }
  };

  // 处理生成知识点并保存
  const handleGenerateKnowledge = async () => {
    if (!exerciseImagePath) {
      setErrorMessage('缺少习题集图片路径');
      return;
    }

    setStepStatus(3, 'loading');
    setIsProcessing(true);
    setErrorMessage(''); // 清除之前的错误

    try {
      // 区分需要重新生成知识点的习题和只需要更新的习题
      const exercisesNeedingAI = exercises.filter(ex => {
        // 如果没有保存过，需要AI生成
        if (!ex.topicId) return true;
        
        // 如果教学要求被修改过，需要重新生成
        if (ex.teacher_requirement !== ex.originalTeacherRequirement) return true;
        
        return false;
      });

      // 检查是否有习题没有提供教学要求
      const exercisesWithoutRequirement = exercisesNeedingAI.filter(
        ex => !ex.teacher_requirement || ex.teacher_requirement.trim() === ''
      );

      // 如果有习题没有教学要求，显示提示信息
      if (exercisesWithoutRequirement.length > 0) {
        const message = exercisesWithoutRequirement.length === exercises.length
          ? '您未提供教学要求，知识点将由AI自主生成'
          : `有 ${exercisesWithoutRequirement.length} 道题未提供教学要求，这些题的知识点将由AI自主生成`;
        
        // 显示提示信息（不阻止操作）
        console.info(message);
      }

      const response = await teacherLearningApi.generateKnowledge({
        exercises,
        exerciseImagePath,
        exercisesNeedingAI: exercisesNeedingAI.map(ex => ex.id), // 传递需要AI处理的习题ID列表
      });

      if (response.success && response.data) {
        // 使用后端返回的完整 exercises 数据（包含 knowledge_points 和 topicId）
        if (response.data.exercises && Array.isArray(response.data.exercises)) {
          // 更新 originalTeacherRequirement 为当前值
          const updatedExercises = response.data.exercises.map((ex: any) => ({
            ...ex,
            originalTeacherRequirement: ex.teacher_requirement,
          }));
          setExercises(updatedExercises);
        }
        
        setStepStatus(3, 'success');
        
        // 显示更详细的成功信息
        const aiCount = response.data.aiGeneratedCount || 0;
        const updateCount = response.data.updatedCount || 0;
        let message = '';
        if (aiCount > 0 && updateCount > 0) {
          message = `成功生成 ${aiCount} 道题目的知识点，更新 ${updateCount} 道题目！`;
        } else if (aiCount > 0) {
          message = `成功生成 ${aiCount} 道题目的知识点！`;
        } else if (updateCount > 0) {
          message = `成功更新 ${updateCount} 道题目！`;
        } else {
          message = `成功保存 ${response.data.count} 道题目！`;
        }
        success(message);
      } else {
        throw new Error(response.error?.message || '保存失败');
      }
    } catch (err: any) {
      setStepStatus(3, 'error');
      
      // 提取详细错误信息
      let errorMessage = '生成知识点并保存失败';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error.message || errorMessage;
        if (err.response.data.error.details) {
          errorMessage += `: ${err.response.data.error.details}`;
        }
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }
      
      setErrorMessage(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  // 处理查看详情
  const handleViewDetail = (exercise: any) => {
    setSelectedExerciseForDetail(exercise);
    setShowKnowledgePointsModal(true);
  };

  // 处理布置作业
  const handleAssignExercises = () => {
    const selectedExercises = exercises.filter((ex) => ex.selected);
    if (selectedExercises.length === 0) {
      setErrorMessage('请先选择要布置的题目');
      return;
    }
    setShowAssignModal(true);
  };

  if (exercises.length === 0) {
    return null;
  }

  const selectedCount = exercises.filter((ex) => ex.selected).length;
  const allSelected = exercises.length > 0 && selectedCount === exercises.length;
  
  // 检查是否所有习题都已保存（有topicId）
  const allExercisesSaved = exercises.every((ex) => ex.topicId);

  return (
    <div className="exercise-cards-section bg-white rounded-lg shadow p-6">
      {/* 头部操作栏 */}
      <div className="section-header flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900">
            识别到的题目（{exercises.length}）
          </h2>
          {allExercisesSaved && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleSelectAll}
                className="w-4 h-4 text-blue-600 rounded"
              />
              全选 {selectedCount > 0 && `(${selectedCount})`}
            </label>
          )}
        </div>

        <div className="actions flex gap-2">
          {currentStep === 3 && (
            <button
              onClick={handleGenerateKnowledge}
              disabled={isProcessing || step3Status === 'loading'}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? '处理中...' : '生成知识点并保存'}
            </button>
          )}
          {step3Status === 'success' && (
            <button
              onClick={handleAssignExercises}
              disabled={selectedCount === 0}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              布置作业 {selectedCount > 0 && `(${selectedCount})`}
            </button>
          )}
        </div>
      </div>

      {/* 教学要求提示 - 显示在状态提示之前 */}
      {currentStep === 3 && step3Status !== 'success' && (() => {
        const exercisesWithoutRequirement = exercises.filter(
          ex => !ex.topicId && (!ex.teacher_requirement || ex.teacher_requirement.trim() === '')
        );
        
        if (exercisesWithoutRequirement.length > 0) {
          const message = exercisesWithoutRequirement.length === exercises.length
            ? '您未提供教学要求，知识点将由AI自主生成'
            : `有 ${exercisesWithoutRequirement.length} 道题未提供教学要求，这些题的知识点将由AI自主生成`;
          
          return (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-start gap-2">
              <span className="text-lg">ℹ️</span>
              <span>{message}</span>
            </div>
          );
        }
        return null;
      })()}

      {/* 状态提示 - 显示在卡片上方 */}
      {step3Status === 'loading' && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center text-sm text-blue-600">
          正在生成知识点并保存，请稍候...
        </div>
      )}
      {step3Status === 'success' && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center text-sm text-green-600">
          ✓ 知识点生成并保存成功
        </div>
      )}
      {step3Status === 'error' && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center text-sm text-red-600">
          ✗ 生成知识点失败，请重试
        </div>
      )}

      {/* 题目卡片列表 - 垂直布局 */}
      <div className="cards-list space-y-4">
        {exercises.map((exercise, index) => (
          <ExerciseCard
            key={exercise.id}
            exercise={exercise}
            index={index}
            onSelect={toggleExerciseSelection}
            onUpdateRequirement={(id, requirement) =>
              updateExercise(id, { teacher_requirement: requirement })
            }
            onViewDetail={handleViewDetail}
            onDelete={deleteExercise}
            showSelection={allExercisesSaved}
          />
        ))}
      </div>

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
